<template lang="pug">
#app.safari-wrap.flex.column
  template(v-if="ready")
    .header.flex(:class="{'show': isLogin}")
      el-tooltip(v-if="version", placement="bottom", :content="version")
        .logo(@click="$copy(version, true)")
      .logo(v-else)
      el-menu.menu(mode="horizontal", :default-active="menu", @select="changePage")
        template(v-if="canShowMenu('Revenue')")
          el-menu-item(index="Revenue", id="menu-Revenue")
            a.link(href="#/revenue") 營收資訊
        el-menu-item(index="Reserve", id="menu-Reserve")
          a.link(href="#/reserve") 預約管理
        el-menu-item(index="Schedule", id="menu-Schedule")
          a.link(href="#/schedule") 行程表
        el-menu-item(index="Space", id="menu-Space")
          a.link(href="#/space") 空間管理
        el-menu-item(index="Users", id="menu-Users")
          a.link(href="#/users") 個案管理
        //- 各頁能否顯示由路由設定的 roles / adminOnly 決定（與路由守衛共用，見 router/access.js）
        template(v-if="canShowMenu('Activity')")
          el-menu-item(index="Activity", id="menu-Activity")
            a.link(href="#/activity") 活動管理
        template(v-if="canShowMenu('Statistics')")
          el-menu-item(index="Statistics", id="menu-Statistics")
            a.link(href="#/statistics") 統計報表
        template(v-if="canShowMenu('Setting')")
          el-menu-item(index="Setting", id="menu-Setting")
            a.link(href="#/setting") 設定清單
      //- 檢視視角中：右上角改成「離開檢視視角」，點擊後切回原本的帳號並回到 debug 頁面
      el-tooltip(v-if="impersonatedBy", placement="bottom", :content="`目前以 ${userDisplayName} 的視角檢視，點擊回到 ${impersonatedBy.name}`")
        .user.leave-impersonation.flex.align-center.gap-12.clickable(@click="doLeaveImpersonation")
          i.el-icon-view
          .username 離開檢視視角
      el-dropdown.header-dropdown(v-else, @command="doHeaderCommand")
        .user.flex.align-center.gap-12
          i.el-icon-user-solid
          .username {{ userDisplayName }}
        el-dropdown-menu(slot="dropdown")
          el-dropdown-item(command="account") 帳號管理
          el-dropdown-item(command="logout") 登出
    router-view.content(v-if="ready")
    template(v-for="(user, idx) in detailUsers")
      user-viewer.user-viewer(
        show-copy
        online-payable-editable
        :uid="user.id"
        :style="{'z-index': idx + 10}"
      )
  //- user-viewer.user-viewer(
  //-   show-copy
  //-   :class="{'show': lastDetailUser !== undefined}"
  //-   @close="lastDetailUser = undefined"
  //-   :uid="lastDetailUser?.id")
  self-pwd-editor(ref="pwdEditor")
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { getProfile } from '@/api/auth';
import { leaveImpersonation } from '@/api/management';
import { getProfessionalList } from '@/api/professionals';
import UserViewer from '@/components/UserViewer.vue';
import SelfPasswordEditorVue from '@/components/drawers/SelfPasswordEditor.vue';
import router from './router';
import { getForbiddenRedirect, isRouteNameAllowed } from './router/access';

/** 超過此時間未點擊，下一次點擊會用 profile API 驗證登入 */
const IDLE_REVERIFY_MS = 10 * 60 * 1000;

export default {
  components: {
    'user-viewer': UserViewer,
    'self-pwd-editor': SelfPasswordEditorVue,
  },
  data() {
    return {
      menu: 'Home',
      ready: false,
      detailUser: undefined,
      detailUsers: [],
      version: undefined,
      idleClickLastAt: Date.now(),
    };
  },
  computed: {
    ...mapGetters(['userDisplayName', 'isLogin', 'user', 'impersonatedBy']),
    lastDetailUser() {
      if (this.detailUsers.length > 0) {
        return this.detailUsers[this.detailUsers.length - 1];
      }
      return undefined;
    },
  },
  watch: {
    $route() {
      this.detailUser = undefined;
      this.detailUsers = [];
      this.checkRoute();
    },
  },
  methods: {
    ...mapMutations(['setUserInfo']),
    doHeaderCommand(cmd) {
      switch (cmd) {
        case 'logout':
          this.logout();
          return;
        case 'account':
          this.$refs.pwdEditor.$emit('show');
          break;
        default:
      }
    },
    logout() {
      this.setUserInfo(undefined);
      this.$router.push('/login');
    },
    async doLeaveImpersonation() {
      await this.$execWithLoading(async () => {
        await leaveImpersonation();
        // 切回原本帳號後重新整理，並回到 debug 頁面
        window.location.hash = '#/debugging';
        window.location.reload();
      }, (e) => {
        this.$showAxiosException('離開檢視視角失敗', e);
      });
    },
    async loadData() {
      return this.$execWithLoading(async () => {
        const profile = await getProfile();
        this.setUserInfo(profile);
      });
    },
    canShowMenu(name) {
      return isRouteNameAllowed(router, name, this.user?.role);
    },
    /** 首次載入取得 profile 後檢查目前頁面權限（之後的換頁由 main.js 的路由守衛處理） */
    async guardCurrentRoute() {
      if (!this.isLogin) {
        return;
      }
      const redirect = getForbiddenRedirect(router, this.$route, this.user.role);
      if (redirect === undefined) {
        return;
      }
      this.$showWarn(this.$msg('noPagePermission'));
      await this.$router.replace(redirect).catch(() => {});
    },
    changePage(key) {
      this.$router.push(`/${key.toLowerCase()}`).catch(this.handleDuplicatedRoute);
    },
    checkRoute() {
      router.forEach((r) => {
        let find = false;
        if (r.name === this.$route.name) {
          find = true;
        }
        if (!find && r.children !== undefined) {
          r.children.forEach((child) => {
            if (child.name === this.$route.name) {
              find = true;
            }
          });
        }
        if (find) {
          this.menu = r.name;
          if (r.needAuth && !this.isLogin) {
            this.$router.push('/login');
          }
        }
      });
    },
    handleDuplicatedRoute(error) {
      if (error.name !== 'NavigationDuplicated') {
        throw error;
      }
      this.ready = false;
      setTimeout(() => {
        this.ready = true;
        this.$forceUpdate();
      }, 100);
    },
    onDocumentClickAfterIdleCheck() {
      const now = Date.now();
      const idleMs = now - this.idleClickLastAt;
      this.idleClickLastAt = now;
      if (idleMs <= IDLE_REVERIFY_MS) {
        return;
      }
      if (!this.isLogin || this.$route.path === '/login') {
        return;
      }
      this.refreshProfileAfterIdleClick();
    },
    async refreshProfileAfterIdleClick() {
      try {
        const profile = await getProfile();
        this.setUserInfo(profile);
      } catch (e) {
        this.logout();
      }
    },
  },
  async mounted() {
    function safariHacks() {
      const windowsVH = window.innerHeight / 100;
      document.querySelector('.safari-wrap').style.setProperty('--vh', `${windowsVH}px`);
      window.addEventListener('resize', () => {
        document.querySelector('.safari-wrap').style.setProperty('--vh', `${windowsVH}px`);
      });
    }

    window.addEventListener('resize', () => {
      safariHacks();
    });

    this.$root.$on('show-user', (user) => {
      this.detailUser = user;
      this.detailUsers.push(user);
    });
    this.$root.$on('close-user', () => {
      if (this.detailUsers.length === 0) {
        this.$router.push('/users');
        return;
      }
      this.detailUsers = this.detailUsers.slice(0, this.detailUsers.length - 1);
    });

    this.version = await this.$getVersion();

    await this.loadData();
    this.checkRoute();
    await this.guardCurrentRoute();
    this.ready = true;
    this.idleClickLastAt = Date.now();
    document.addEventListener('click', this.onDocumentClickAfterIdleCheck, true);
    await getProfessionalList();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClickAfterIdleCheck, true);
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: $text-regular-color;
  font-size: 14px;
  .header {
    max-height: 0;
    transition: transform .3s ease-in-out;
    border-bottom: solid 1px #e6e6e6;
    overflow: hidden;

    &.show {
      max-height: $header-height;
    }
    .logo {
      flex: 0 0 82px;
      background-image: url('/logo.png');
      background-position: 50% 50%;
      background-size: 40px 35px;
      background-repeat: no-repeat;
    }
    .menu {
      flex: 1;
      border: none;
      .link {
        text-decoration: none;
      }
    }
    .user {
      padding: 0 20px;
      &.leave-impersonation {
        color: #E6A23C;
        font-weight: bold;
      }
    }
    .logout {
      padding: 0 20px;
      color: $text-secondary-color;
    }
  }
  .content {
    flex: 1;
    overflow-x: hidden;
    background: #F9F9F9;
  }
  .user-viewer {
    position: absolute;
    opacity: 1;
    pointer-events: all;
    transition: all .5s ease-in-out;
    top: $header-height;
    left: 0;
    z-index: 2;
  }
  .header-dropdown {
    display: flex;
    cursor: pointer;
  }
}

</style>
